import { EventEmitter } from 'events';
import { EditorClient } from './editor-client.js';
import { EntityManager } from './entity-manager.js';
import { TextOperation } from './text-operation.js';
import { ServerAdapter } from './adapter.js';
import { RichTextCodeMirror } from './rich-text-codemirror.js';
import { RichTextCodeMirrorAdapter } from './rich-text-codemirror-adapter.js';
import { CodeMirror6Adapter } from './codemirror6-adapter.js';

export { EditorClient } from './editor-client.js';
export { ServerAdapter } from './adapter.js';
export { TextOperation } from './text-operation.js';



const DEFAULT_OPTIONS = {userId: 'null-user', userColor: '#9999FF'};


class FirepadCore extends EditorClient {

  constructor(editor, opts={}) {
    opts = Object.assign({}, DEFAULT_OPTIONS, opts);

    var serverAdapter = new TriggerAdapter(null, opts.userId, opts.userColor),
        editorAdapter = FirepadCore.createEditorAdapter(editor, opts);

    super(serverAdapter, editorAdapter);
    serverAdapter.target = this; /* `this` is only available after super() */

    this.editor = editor;
    this.events = new EventEmitter;
    this.cursorMonitor = new CursorMonitor(serverAdapter);
  }

  setUserId(userId)   { this.serverAdapter.setUserId(userId); }
  setUserColor(color) { this.serverAdapter.setUserColor(color); }

  static createEditorAdapter(editor, opts={}) {
    switch (opts.type ?? editor.constructor.name) {
      case 'CodeMirror':
      case 'CodeMirror5': return FirepadCore.createCodeMirror5Adapter(editor, opts);
      case 'EditorView':
      case 'CodeMirror6': return FirepadCore.createCodeMirror6Adapter(editor, opts);
      default:
        throw new Error(`unable to detect editor (got: '${editor.constructor.name}'`);
    }
  }

  static createCodeMirror6Adapter(cm, _opts) {
    return new CodeMirror6Adapter(cm);
  }

  static createCodeMirror5Adapter(cm, _opts) {
    var entityManager_ = new EntityManager();
    var richTextCodeMirror_ = new RichTextCodeMirror(cm, entityManager_,
                              { cssPrefix: 'firepad-' }),
        editorAdapter_ = new RichTextCodeMirrorAdapter(richTextCodeMirror_);

    return editorAdapter_;
  }

  data(msg) {
    if (msg.operation)
      this.serverAdapter.trigger('operation', this._op(msg.operation));
    if (msg.cursor)
      this.serverAdapter.trigger('cursor',
        msg.cursor.user, msg.cursor.at, msg.cursor.color, msg.cursor.tm);
  }

  on(eventType, handler)  { this.events.on(eventType, handler); }
  off(eventType, handler) { this.events.off(eventType, handler); }
  trigger(eventType, ...eventData) { this.events.emit(eventType, ...eventData); }

  _op(operation) {
    return (operation instanceof TextOperation) ? 
            operation : TextOperation.fromJSON(operation);
  }

  dispose() {
    this.onBlur();
    this.destroyed = true;
    this.serverAdapter.dispose();
    this.editorAdapter.detach();
    if (this.editorAdapter.rtcm) this.editorAdapter.rtcm.detach();
    this.cursorMonitor.dispose();
  }
}


class TriggerAdapter extends ServerAdapter {

  constructor(target, userId, userColor) {
    super(userId, userColor);
    this.target = target;
  }

  sendOperation(operation, callback) {
    var event = new OperationEvent(this.target, this, operation, callback);
    this.target.trigger('data', event);
    if (!event.isDefaultPrevented) {
      process.nextTick(() => event.accept());
    }
  }

  sendCursor(at) {
    var cursor = {at, user: this.userId_, color: this.userColor_, tm: +new Date()};
    this.target.trigger('data', {cursor});
  }
}


class OperationEvent {

  constructor(target, adapter, operation, callback) {
    this.target = target;
    this.adapter = adapter;
    this.operation = operation;
    this.callback = callback;
    this.isDefaultPrevented = false;
  }

  accept() {
    this.adapter.trigger('ack');
    if (this.callback) this.callback.apply(null);
  }

  reject() {
    this.adapter.trigger('retry');
    if (this.callback) this.callback.apply(null);  // different args?
  }

  preventDefault() {
    this.isDefaultPrevented = true;
  }
}


/**
 * Keeps track of life signs from users' cursors and cleans up stale
 * entries.
 */
class CursorMonitor {

  constructor(serverAdapter) {
    this.serverAdapter = serverAdapter;
    this.timestamps = new Map();  // [user, last cursor timestamp]
    this.retireTimeout = CursorMonitor.RETIRE_TIMEOUT;
    this.serverAdapter.on('cursor', (user, at, _, tm) => this.record(user, at, tm));
    this.watchdog = setInterval(() => this.cleanup(), CursorMonitor.WATCHDOG_INTERVAL);
  }

  record(user, at, tm) {
    this.timestamps.set(user, tm ?? +new Date());
  }

  cleanup() {
    let now = +new Date(),
        old = [...this.timestamps.entries()]
              .filter(([_, v]) => !v || now - v > this.retireTimeout);
    for (let [k, _] of old) {
      this.serverAdapter.emit('retire', k);
      this.serverAdapter.emit('cursor', k, null, null);
      this.timestamps.delete(k); /* fugly, needs to occur after emit */
    }
  }

  dispose() {
    clearInterval(this.watchdog);
  }

  static RETIRE_TIMEOUT = 9 * 1000 /* ms */;
  static WATCHDOG_INTERVAL = 6 * 1000 /* ms */
}


export { FirepadCore, TriggerAdapter, OperationEvent }
